import {
  Button, Dialog, DialogContent, IconButton, InputAdornment, 
  TextField, Typography, DialogActions, DialogTitle, Stack,
  Alert, CircularProgress
} from '@mui/material';
import { useMeCore } from '../../core/modules/me';
import { useGlobalCore } from '../../core/modules/global';
import { ME_PASSWORD_DIALOG } from '../../core/constants/dialog';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { MouseEvent, useCallback, useState, useEffect } from 'react';
import { VisibilityOff, Visibility, VpnKey } from '@mui/icons-material';
import { PutMePasswordPayloadRequest } from '../../core/modules/me/interfaces/put-me-password';

// Definición del tipo para los datos del formulario
interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string; // Añadido para confirmación
}

// Esquema de validación actualizado
const schema = yup.object().shape({
  currentPassword: yup.string().required('Debes ingresar tu contraseña actual'),
  newPassword: yup.string()
    .required('Debes ingresar la nueva contraseña')
    .min(8, 'La nueva contraseña debe tener al menos 8 caracteres'),
  confirmNewPassword: yup.string()
    .required('Debes confirmar la nueva contraseña')
    .oneOf([yup.ref('newPassword')], 'Las contraseñas no coinciden'), // Comprueba que coincida con newPassword
});

const defaultValues: FormData = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

function ChangePasswordDialog() {
  const { t } = useTranslation();
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { putMePassword } = useMeCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, isDirty, errors } = formState;
  
  const handleClose = () => {
    reset(defaultValues);
    setApiError(null);
    closeDialog();
  }

  // Se usa SubmitHandler para un tipado más estricto
  const onSubmit: SubmitHandler<FormData> = useCallback(
    async ({ newPassword, currentPassword }) => {
      setIsSubmitting(true);
      setApiError(null);

      const payload: PutMePasswordPayloadRequest = { newPassword, currentPassword };

      await putMePassword(payload,
        () => { // onSuccess
          showSnackbar({ message: 'Contraseña actualizada con éxito.', options: { variant: 'success' } });
          handleClose();
        },
        (error: any) => { // onFailure
          // Extraer el mensaje específico del backend
          const message = error.response?.data?.detail || 'Ocurrió un error inesperado.';
          setApiError(message);
        }
      );
      
      setIsSubmitting(false);
    },
    [putMePassword, showSnackbar, handleClose]
  );

  return (
    <Dialog open={dialog === ME_PASSWORD_DIALOG} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" component="div" className="font-bold flex items-center gap-2">
          <VpnKey />
          Cambiar Contraseña
        </Typography>
      </DialogTitle>
      
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Controller name="currentPassword" control={control} render={({ field }) => (
              <TextField {...field} label="Contraseña actual" type={showPassword ? 'text' : 'password'}
                error={!!errors.currentPassword} helperText={errors.currentPassword?.message} required fullWidth autoFocus
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
            )} />

            <Controller name="newPassword" control={control} render={({ field }) => (
              <TextField {...field} label="Nueva contraseña" type={showPassword ? 'text' : 'password'}
                error={!!errors.newPassword} helperText={errors.newPassword?.message} required fullWidth
              />
            )} />

            <Controller name="confirmNewPassword" control={control} render={({ field }) => (
              <TextField {...field} label="Confirmar nueva contraseña" type={showPassword ? 'text' : 'password'}
                error={!!errors.confirmNewPassword} helperText={errors.confirmNewPassword?.message} required fullWidth
              />
            )} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ChangePasswordDialog;